import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/store/useAppStore";
import { watchRooms, type RoomListing } from "@/client/session";
import { MAX_PLAYERS } from "@/shared/match";

export const Route = createFileRoute("/join")({
  component: Join,
});

function Join() {
  const pseudo = useAppStore((state) => state.pseudo);
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<RoomListing[] | null>(null);
  const [unreachable, setUnreachable] = useState(false);

  useEffect(() => {
    if (!pseudo) navigate({ to: "/" });
  }, [pseudo, navigate]);

  useEffect(() => {
    let alive = true;
    let unwatch: (() => void) | undefined;

    watchRooms((found) => {
      if (alive) setRooms(found);
    })
      .then((dispose) => {
        // Démontage arrivé pendant que le join de la LobbyRoom était en vol :
        // on referme tout de suite, sinon on laisse une connexion ouverte.
        if (alive) unwatch = dispose;
        else dispose();
      })
      .catch((err) => {
        console.warn("[session] liste des parties indisponible", err);
        if (alive) setUnreachable(true);
      });

    return () => {
      alive = false;
      unwatch?.();
    };
  }, []);

  return (
    <main className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-6 px-4">
      <header className="text-center">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Parties en attente</h1>
        <p className="text-sm text-muted-foreground">
          Seules les parties non commencées apparaissent ici.
        </p>
      </header>

      {unreachable && (
        <p role="alert" className="text-center text-sm text-destructive">
          Le serveur ne répond pas.
        </p>
      )}

      {rooms === null && !unreachable && (
        <p className="text-center text-sm text-muted-foreground">Recherche...</p>
      )}

      {rooms?.length === 0 && (
        <p className="text-center text-sm text-muted-foreground">
          Aucune partie en attente. Crée la tienne.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {rooms?.map((room) => (
          <li
            key={room.roomId}
            className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
          >
            <div className="flex flex-col text-left">
              <span className="font-mono text-sm">{room.roomId}</span>
              <span className="text-xs text-muted-foreground">
                {room.clients}/{room.maxClients || MAX_PLAYERS} ninjas
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => navigate({ to: "/lobby/$roomId", params: { roomId: room.roomId } })}
            >
              Rejoindre
            </Button>
          </li>
        ))}
      </ul>

      <Button variant="outline" onClick={() => navigate({ to: "/" })}>
        Retour
      </Button>
    </main>
  );
}
